import { z } from "zod";

// =====================================================================
// FDI / ISO-3950 tooth numbering
// =====================================================================
// Quadrant layout as shown on a chart (patient's mouth, dentist's view):
//   Upper:  18..11 | 21..28      Lower:  48..41 | 31..38
// Primary teeth use quadrants 5–8:
//   Upper:  55..51 | 61..65      Lower:  85..81 | 71..75
//
// Arrays are ordered left→right as drawn, so the UI can render each
// quadrant row directly.

/** Permanent (adult) — 32 teeth, by quadrant row. */
export const PERMANENT_UPPER_RIGHT = [18, 17, 16, 15, 14, 13, 12, 11] as const;
export const PERMANENT_UPPER_LEFT = [21, 22, 23, 24, 25, 26, 27, 28] as const;
export const PERMANENT_LOWER_RIGHT = [48, 47, 46, 45, 44, 43, 42, 41] as const;
export const PERMANENT_LOWER_LEFT = [31, 32, 33, 34, 35, 36, 37, 38] as const;

/** Primary (deciduous / baby) — 20 teeth, by quadrant row. */
export const PRIMARY_UPPER_RIGHT = [55, 54, 53, 52, 51] as const;
export const PRIMARY_UPPER_LEFT = [61, 62, 63, 64, 65] as const;
export const PRIMARY_LOWER_RIGHT = [85, 84, 83, 82, 81] as const;
export const PRIMARY_LOWER_LEFT = [71, 72, 73, 74, 75] as const;

export const PERMANENT_TEETH: readonly number[] = [
	...PERMANENT_UPPER_RIGHT,
	...PERMANENT_UPPER_LEFT,
	...PERMANENT_LOWER_RIGHT,
	...PERMANENT_LOWER_LEFT,
];
export const PRIMARY_TEETH: readonly number[] = [
	...PRIMARY_UPPER_RIGHT,
	...PRIMARY_UPPER_LEFT,
	...PRIMARY_LOWER_RIGHT,
	...PRIMARY_LOWER_LEFT,
];

/** Every valid FDI tooth number (permanent + primary). */
export const ALL_FDI_TEETH: ReadonlySet<number> = new Set([
	...PERMANENT_TEETH,
	...PRIMARY_TEETH,
]);

export function isValidToothNumber(n: number): boolean {
	return ALL_FDI_TEETH.has(n);
}
export function isPrimaryTooth(n: number): boolean {
	return PRIMARY_TEETH.includes(n);
}

const toothNumberSchema = z
	.number()
	.int()
	.refine(isValidToothNumber, { message: "Invalid FDI tooth number" });

// =====================================================================
// Enums (mirror the DB pgEnums)
// =====================================================================

export const toothSurfaceSchema = z.enum([
	"mesial",
	"distal",
	"buccal",
	"lingual",
	"occlusal",
]);
export type ToothSurface = z.infer<typeof toothSurfaceSchema>;

export const dentalConditionTypeSchema = z.enum([
	"caries",
	"filling",
	"crown",
	"bridge",
	"veneer",
	"root_canal",
	"implant",
	"missing",
	"extraction_needed",
	"fracture",
	"sealant",
	"impacted",
	"other",
]);
export type DentalConditionType = z.infer<typeof dentalConditionTypeSchema>;

export const dentalConditionStatusSchema = z.enum([
	"existing",
	"planned",
	"completed",
]);
export type DentalConditionStatus = z.infer<typeof dentalConditionStatusSchema>;

export const dentalTreatmentStatusSchema = z.enum([
	"planned",
	"in_progress",
	"completed",
	"cancelled",
]);
export type DentalTreatmentStatus = z.infer<typeof dentalTreatmentStatusSchema>;

/** Condition types that are recorded against a specific tooth surface. */
export const SURFACE_CONDITIONS: readonly DentalConditionType[] = [
	"caries",
	"filling",
	"sealant",
	"fracture",
];

// =====================================================================
// Odontogram — tooth conditions
// =====================================================================

export const toothConditionSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	tooth_number: z.number().int(),
	surface: toothSurfaceSchema.nullable(),
	condition_type: dentalConditionTypeSchema,
	status: dentalConditionStatusSchema,
	notes: z.string().nullable(),
	/** Visit this finding was charted in (null = recorded outside an encounter). */
	visit_id: z.string().uuid().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type ToothConditionApi = z.infer<typeof toothConditionSchema>;

export const createToothConditionInputSchema = z.object({
	patient_id: z.string().uuid(),
	tooth_number: toothNumberSchema,
	surface: toothSurfaceSchema.nullable().optional(),
	condition_type: dentalConditionTypeSchema,
	status: dentalConditionStatusSchema.default("existing"),
	notes: z.string().max(1000).optional(),
	/** When charted during an encounter — stamps the record's visit link. */
	appointment_id: z.string().uuid().optional(),
});
export type CreateToothConditionInput = z.infer<
	typeof createToothConditionInputSchema
>;

export const updateToothConditionInputSchema = z.object({
	surface: toothSurfaceSchema.nullable().optional(),
	condition_type: dentalConditionTypeSchema.optional(),
	status: dentalConditionStatusSchema.optional(),
	notes: z.string().max(1000).nullable().optional(),
});
export type UpdateToothConditionInput = z.infer<
	typeof updateToothConditionInputSchema
>;

// =====================================================================
// Treatment plan
// =====================================================================

export const treatmentPlanItemSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	tooth_number: z.number().int().nullable(),
	surface: toothSurfaceSchema.nullable(),
	procedure_name: z.string(),
	service_id: z.string().uuid().nullable(),
	cost: z.string(), // numeric(15,2) as string — no JS float drift
	currency: z.string(),
	status: dentalTreatmentStatusSchema,
	invoice_id: z.string().uuid().nullable(),
	notes: z.string().nullable(),
	/** Visit this item was planned in (null = outside an encounter). */
	visit_id: z.string().uuid().nullable(),
	completed_at: z.string().datetime().nullable(),
	sort_order: z.number().int(),
	created_at: z.string().datetime(),
	updated_at: z.string().datetime(),
});
export type TreatmentPlanItemApi = z.infer<typeof treatmentPlanItemSchema>;

export const createTreatmentPlanItemInputSchema = z.object({
	patient_id: z.string().uuid(),
	tooth_number: toothNumberSchema.nullable().optional(),
	surface: toothSurfaceSchema.nullable().optional(),
	procedure_name: z.string().min(1).max(200),
	service_id: z.string().uuid().nullable().optional(),
	cost: z.number().min(0).max(999_999_999),
	status: dentalTreatmentStatusSchema.default("planned"),
	notes: z.string().max(1000).optional(),
	/** When planned during an encounter — stamps the record's visit link. */
	appointment_id: z.string().uuid().optional(),
});
export type CreateTreatmentPlanItemInput = z.infer<
	typeof createTreatmentPlanItemInputSchema
>;

export const updateTreatmentPlanItemInputSchema = z.object({
	tooth_number: toothNumberSchema.nullable().optional(),
	surface: toothSurfaceSchema.nullable().optional(),
	procedure_name: z.string().min(1).max(200).optional(),
	service_id: z.string().uuid().nullable().optional(),
	cost: z.number().min(0).max(999_999_999).optional(),
	status: dentalTreatmentStatusSchema.optional(),
	notes: z.string().max(1000).nullable().optional(),
	sort_order: z.number().int().optional(),
});
export type UpdateTreatmentPlanItemInput = z.infer<
	typeof updateTreatmentPlanItemInputSchema
>;

/**
 * Convert one or more *completed*, *un-billed* treatment items into a single
 * invoice. Each item becomes an invoice line (procedure name + cost) and is
 * stamped with the new invoice_id so it can't be billed twice.
 */
export const convertTreatmentItemsToInvoiceInputSchema = z.object({
	patient_id: z.string().uuid(),
	item_ids: z.array(z.string().uuid()).min(1).max(50),
	issue_immediately: z.boolean().default(false),
	discount_amount: z.number().min(0).default(0),
	tax_amount: z.number().min(0).default(0),
	notes: z.string().max(2000).optional(),
});
export type ConvertTreatmentItemsToInvoiceInput = z.infer<
	typeof convertTreatmentItemsToInvoiceInputSchema
>;

/** Full chart payload for a patient (one round-trip). */
export const dentalChartSchema = z.object({
	conditions: z.array(toothConditionSchema),
	treatment_items: z.array(treatmentPlanItemSchema),
});
export type DentalChartApi = z.infer<typeof dentalChartSchema>;

// =====================================================================
// Periodontal charting (probing depths + bleeding on probing + mobility)
// =====================================================================

/**
 * The six standard probing sites per tooth, in charting order:
 * distobuccal, buccal, mesiobuccal, distolingual, lingual, mesiolingual.
 */
export const PERIO_SITES = ["db", "b", "mb", "dl", "l", "ml"] as const;
export type PerioSite = (typeof PERIO_SITES)[number];

export const perioToothSchema = z.object({
	tooth: z.number().int(),
	/** Pocket depth in mm at each of the 6 sites (0 = not measured). */
	pd: z.array(z.number().int().min(0).max(20)).length(6),
	/** Bleeding-on-probing flag at each of the 6 sites. */
	bop: z.array(z.boolean()).length(6),
	/** Miller mobility grade 0–3. */
	mobility: z.number().int().min(0).max(3).default(0),
	/** Optional gingival recession in mm (worst site). */
	recession: z.number().int().min(0).max(20).nullable().optional(),
});
export type PerioTooth = z.infer<typeof perioToothSchema>;

export const periodontalExamApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	exam_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	teeth: z.array(perioToothSchema),
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type PeriodontalExamApi = z.infer<typeof periodontalExamApiSchema>;

export const createPeriodontalExamInputSchema = z.object({
	patient_id: z.string().uuid(),
	exam_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional(),
	teeth: z.array(perioToothSchema).min(1).max(32),
	notes: z.string().max(1000).optional(),
});
export type CreatePeriodontalExamInput = z.infer<
	typeof createPeriodontalExamInputSchema
>;

export type PerioStage =
	| "healthy"
	| "gingivitis"
	| "mild"
	| "moderate"
	| "severe";

export interface PerioSummary {
	sitesTotal: number;
	bopCount: number;
	bopPct: number;
	sites4to5: number;
	sites6plus: number;
	meanPd: number;
	maxMobility: number;
	stage: PerioStage;
}

/**
 * Summarise a periodontal exam. Staging is a pragmatic screen (not full AAP
 * 2017 staging): pockets ≥6 mm → severe, 4–5 mm → moderate/mild by extent,
 * BOP-only → gingivitis. Pure.
 */
export function perioSummary(teeth: PerioTooth[]): PerioSummary {
	let sitesTotal = 0;
	let bopCount = 0;
	let sites4to5 = 0;
	let sites6plus = 0;
	let pdSum = 0;
	let maxMobility = 0;
	for (const t of teeth) {
		maxMobility = Math.max(maxMobility, t.mobility ?? 0);
		for (let i = 0; i < 6; i++) {
			const pd = t.pd[i] ?? 0;
			const measured = pd > 0;
			if (measured) {
				sitesTotal++;
				pdSum += pd;
				if (pd >= 6) sites6plus++;
				else if (pd >= 4) sites4to5++;
				// Bleeding is only meaningful on a site that was actually probed;
				// counting it on unmeasured sites let BOP% exceed 100%.
				if (t.bop[i]) bopCount++;
			}
		}
	}
	const bopPct = sitesTotal > 0 ? Math.round((bopCount / sitesTotal) * 100) : 0;
	const meanPd =
		sitesTotal > 0 ? Math.round((pdSum / sitesTotal) * 10) / 10 : 0;
	let stage: PerioStage = "healthy";
	if (sites6plus > 0) stage = "severe";
	else if (sites4to5 >= 3) stage = "moderate";
	else if (sites4to5 > 0) stage = "mild";
	else if (bopPct >= 10) stage = "gingivitis";
	return {
		sitesTotal,
		bopCount,
		bopPct,
		sites4to5,
		sites6plus,
		meanPd,
		maxMobility,
		stage,
	};
}

// =====================================================================
// Recall / prevention reminders
// =====================================================================

export const dentalRecallReasonSchema = z.enum([
	"cleaning",
	"checkup",
	"perio_maintenance",
	"ortho_review",
	"post_treatment",
	"other",
]);
export type DentalRecallReason = z.infer<typeof dentalRecallReasonSchema>;

export const dentalRecallStatusSchema = z.enum([
	"scheduled",
	"completed",
	"cancelled",
]);
export type DentalRecallStatus = z.infer<typeof dentalRecallStatusSchema>;

export const dentalRecallApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	reason: dentalRecallReasonSchema,
	status: dentalRecallStatusSchema,
	notes: z.string().nullable(),
	recorded_by_member_id: z.string().uuid().nullable(),
	created_at: z.string().datetime(),
});
export type DentalRecallApi = z.infer<typeof dentalRecallApiSchema>;

export const createDentalRecallInputSchema = z.object({
	patient_id: z.string().uuid(),
	due_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	reason: dentalRecallReasonSchema.default("checkup"),
	notes: z.string().max(500).optional(),
});
export type CreateDentalRecallInput = z.infer<
	typeof createDentalRecallInputSchema
>;

export const updateDentalRecallInputSchema = z.object({
	due_date: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional(),
	reason: dentalRecallReasonSchema.optional(),
	status: dentalRecallStatusSchema.optional(),
	notes: z.string().max(500).nullable().optional(),
});
export type UpdateDentalRecallInput = z.infer<
	typeof updateDentalRecallInputSchema
>;

// =====================================================================
// Per-tooth images (intra-oral photos + radiographs)
// =====================================================================

export const dentalImageKindSchema = z.enum(["photo", "xray"]);
export type DentalImageKind = z.infer<typeof dentalImageKindSchema>;

const dentalImageContentType = z.enum([
	"image/jpeg",
	"image/png",
	"image/webp",
	"application/pdf",
]);

export const dentalToothImagePresignInputSchema = z.object({
	tooth_number: toothNumberSchema,
	kind: dentalImageKindSchema.default("photo"),
	content_type: dentalImageContentType,
	byte_size: z
		.number()
		.int()
		.positive()
		.max(25 * 1024 * 1024), // 25 MB cap
	label: z.string().max(120).optional(),
});
export type DentalToothImagePresignInput = z.infer<
	typeof dentalToothImagePresignInputSchema
>;

export const dentalToothImagePresignResponseSchema = z.object({
	upload_url: z.string().url(),
	r2_key: z.string(),
	expires_in: z.number().int(),
});
export type DentalToothImagePresignResponse = z.infer<
	typeof dentalToothImagePresignResponseSchema
>;

export const confirmDentalToothImageInputSchema = z.object({
	tooth_number: toothNumberSchema,
	kind: dentalImageKindSchema.default("photo"),
	r2_key: z.string().min(1).max(500),
	content_type: z.string().min(1).max(120),
	byte_size: z.number().int().positive().nullable().optional(),
	label: z.string().max(120).nullable().optional(),
	/** When captured during an encounter — stamps the record's visit link. */
	appointment_id: z.string().uuid().optional(),
});
export type ConfirmDentalToothImageInput = z.infer<
	typeof confirmDentalToothImageInputSchema
>;

export const dentalToothImageSchema = z.object({
	id: z.string().uuid(),
	patient_id: z.string().uuid(),
	tooth_number: z.number().int(),
	kind: dentalImageKindSchema,
	r2_key: z.string(),
	content_type: z.string(),
	byte_size: z.number().nullable(),
	label: z.string().nullable(),
	/** Visit this image was captured in (null = outside an encounter). */
	visit_id: z.string().uuid().nullable(),
	created_at: z.string(),
	/** Presigned download URL, generated on read; short-lived. */
	download_url: z.string().url().optional(),
});
export type DentalToothImageApi = z.infer<typeof dentalToothImageSchema>;
